import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import { CATALOG_VALUELESS_OPERATORS } from '@erp/rpc';
import type {
  CatalogAdvancedFilter,
  ValidationRuleDTO,
  ValidationRunDTO,
} from '@erp/rpc';
import { CorrectionStrategy, ValidationRuleTrigger } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { useAllDataSourceLinks } from '@/api/rpc/hooks/core/useDataSources';
import { useCatalogFilterOperatorLabels } from '../../components/AdvancedFilters';
import { nextCronRun } from '../validationScheduleCron';
import {
  RunOutcomeBadge,
  formatScheduleTime,
  formatValidationTimestamp,
  useCorrectionStrategyLabels,
  useRuleTypeLabels,
  useTriggerLabels,
} from './validationRuleDisplay';

/**
 * A scheduled rule's cron, with the next fire time it implies.
 *
 * The cron is echoed verbatim because it is what the author typed and what a
 * runbook will ask them to check; the next-run line is the part that makes a
 * typo obvious. Both are computed here rather than sent from the server — the
 * frontend already parses cron with the same library for the workflows list.
 *
 * A cron that no longer parses is shown as-is with no next run, which is exactly
 * the state worth noticing: the rule is active and will never fire.
 */
function RuleSchedule({ scheduleCron }: { scheduleCron: string | null }) {
  if (!scheduleCron) return null;
  const nextRun = nextCronRun(scheduleCron);

  return (
    <div className="mt-0.5 space-y-0.5">
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
        {scheduleCron}
      </code>
      <p className="text-xs text-muted-foreground">
        {nextRun ? (
          <>
            <T context="label for when a scheduled validation rule will next run">
              Next run
            </T>{' '}
            {formatScheduleTime(nextRun)}
          </>
        ) : (
          <T context="warning that a validation rule's schedule cannot be read">
            This schedule cannot be read, so the rule will not run on its own.
          </T>
        )}
      </p>
    </div>
  );
}

/** One labelled row of the definition summary. */
function DefinitionRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid gap-1 py-2.5 sm:grid-cols-[11rem_1fr] sm:gap-3">
      <dt className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/** Field codes as chips, in the order the author picked them. */
function FieldChips({ fields }: { fields: string[] }) {
  if (fields.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {fields.map((field) => (
        <Badge key={field} variant="secondary" className="font-mono">
          {field}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The rule's row filters, read-only. A value-less operator ("Is empty") prints
 * without one, matching the editor that authored it.
 */
function RowFilterList({ filters }: { filters: CatalogAdvancedFilter[] }) {
  const operatorLabels = useCatalogFilterOperatorLabels();

  if (filters.length === 0) {
    return (
      <span className="text-muted-foreground">
        <T context="a validation rule with no row filters reads the whole table">
          Every row of the table.
        </T>
      </span>
    );
  }

  return (
    <ul className="space-y-1">
      {filters.map((filter, index) => (
        <li
          key={`${filter.column}-${filter.operator}-${index}`}
          className="flex flex-wrap items-baseline gap-1.5"
        >
          <span className="font-mono text-sm">{filter.column}</span>
          <span className="text-xs text-muted-foreground">
            {operatorLabels[filter.operator]}
          </span>
          {!CATALOG_VALUELESS_OPERATORS.has(filter.operator) && (
            <span className="font-mono text-sm">{filter.value}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Which of the three comparison normalizations the rule applies. */
function NormalizationList({ rule }: { rule: ValidationRuleDTO }) {
  const gt = useGT();
  const applied: string[] = [];

  if (rule.normalization.trimWhitespace) {
    applied.push(
      gt('Ignore leading and trailing spaces', {
        context: 'normalization applied before comparing two cell values',
      }),
    );
  }
  if (rule.normalization.caseInsensitive) {
    applied.push(
      gt('Ignore letter case', {
        context: 'normalization applied before comparing two cell values',
      }),
    );
  }
  if (rule.normalization.blankEqualsNull) {
    applied.push(
      gt('Treat a blank cell as missing', {
        context: 'normalization applied before comparing two cell values',
      }),
    );
  }

  if (applied.length === 0) {
    return (
      <span className="text-muted-foreground">
        <T context="a validation rule that compares cell values exactly as stored">
          Compare values exactly as stored.
        </T>
      </span>
    );
  }

  return (
    <ul className="list-inside list-disc space-y-0.5">
      {applied.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

/** The division whose value wins, named as the org names it. */
function PreferredDivision({ dataSourceId }: { dataSourceId: string }) {
  const { data } = useAllDataSourceLinks();
  const title = (data?.groups ?? []).find((group) =>
    group.links.some((link) => link.dataSourceId === dataSourceId),
  )?.sourceTitle;

  return (
    <span className="text-muted-foreground">
      {title ?? (
        <T context="fallback when the preferred division of a validation rule can no longer be named">
          a division that no longer exists
        </T>
      )}
    </span>
  );
}

/** The rule's assignee, resolved against the org's users. */
function AssigneeName({ userId }: { userId: string }) {
  const { data: users } = useUsersInOrg();
  const user = users?.find((candidate) => candidate.id === userId);

  if (user) return <>{user.name}</>;
  return (
    <span className="text-muted-foreground">
      <T context="fallback when a validation rule's assignee is no longer in the org">
        A user who has left
      </T>
    </span>
  );
}

/**
 * What the rule actually checks, as the builder asked it: the table and rows it
 * reads, how it groups them, which fields have to agree, how values are compared,
 * what it proposes for a disagreement, and who owns the result.
 */
export function ValidationRuleDefinitionCard({
  rule,
  lastRun,
}: {
  rule: ValidationRuleDTO;
  /**
   * The rule's most recent run, if the page has loaded its history. Read from the
   * run list already on screen rather than fetched again — it is the first row of
   * a query the page is making anyway.
   */
  lastRun?: ValidationRunDTO | null;
}) {
  const ruleTypeLabels = useRuleTypeLabels();
  const strategyLabels = useCorrectionStrategyLabels();
  const triggerLabels = useTriggerLabels();
  const preferredDataSourceId = rule.strategyConfig?.preferredDataSourceId;

  return (
    <section className="rounded-lg border">
      <h2 className="border-b px-3 py-2 text-sm font-medium">
        <T context="heading of the definition summary on a validation rule's page">
          What this rule checks
        </T>
      </h2>
      <dl className="divide-y px-3 py-1">
        <DefinitionRow
          label={
            <T context="definition row label, the AS/400 table a rule validates">
              Table
            </T>
          }
        >
          <span className="font-mono">{rule.schemaName}</span>
          <span className="ml-2 text-muted-foreground">
            {ruleTypeLabels[rule.ruleType]}
          </span>
        </DefinitionRow>

        <DefinitionRow
          label={
            <T context="definition row label, which rows a rule reads">Rows</T>
          }
        >
          <RowFilterList filters={rule.rowFilters ?? []} />
        </DefinitionRow>

        <DefinitionRow
          label={
            <T context="definition row label, the fields rows are grouped by">
              Grouped by
            </T>
          }
        >
          <FieldChips fields={rule.groupingFields} />
        </DefinitionRow>

        <DefinitionRow
          label={
            <T context="definition row label, the fields that must agree within a group">
              Must agree
            </T>
          }
        >
          <FieldChips fields={rule.validatedFields} />
        </DefinitionRow>

        <DefinitionRow
          label={
            <T context="definition row label, how cell values are normalized before comparison">
              Comparison
            </T>
          }
        >
          <NormalizationList rule={rule} />
        </DefinitionRow>

        <DefinitionRow
          label={
            <T context="definition row label, what a rule proposes for a disagreement">
              Correction
            </T>
          }
        >
          {strategyLabels[rule.correctionStrategy]}
          {rule.correctionStrategy === CorrectionStrategy.PreferredSource &&
            preferredDataSourceId && (
              <>
                {' · '}
                <PreferredDivision dataSourceId={preferredDataSourceId} />
              </>
            )}
        </DefinitionRow>

        <DefinitionRow
          label={<T context="definition row label, when a rule runs">Runs</T>}
        >
          {triggerLabels[rule.executionTrigger]}
          {rule.executionTrigger === ValidationRuleTrigger.Scheduled && (
            <RuleSchedule scheduleCron={rule.scheduleCron} />
          )}
        </DefinitionRow>

        {/* `undefined` means the caller doesn't know — omit rather than guess. */}
        {lastRun !== undefined && (
          <DefinitionRow
            label={
              <T context="definition row label, when a rule last ran">
                Last run
              </T>
            }
          >
            {lastRun ? (
              <span className="inline-flex items-center gap-2">
                {formatValidationTimestamp(lastRun.startedAt)}
                <RunOutcomeBadge status={lastRun.status} />
              </span>
            ) : (
              <span className="text-muted-foreground">
                <T context="a validation rule that has never been run">Never</T>
              </span>
            )}
          </DefinitionRow>
        )}

        <DefinitionRow
          label={
            <T context="definition row label, who owns a validation rule">
              Assignee
            </T>
          }
        >
          {rule.assigneeUserId ? (
            <AssigneeName userId={rule.assigneeUserId} />
          ) : (
            <span className="text-muted-foreground">
              <T context="a validation rule with nobody assigned to it">
                Nobody
              </T>
            </span>
          )}
        </DefinitionRow>
      </dl>
    </section>
  );
}
